/**
 * MODEL PRECISION round (Matt, 2026-10-08) — DATUMS ALWAYS ON THE LATTICE.
 *
 * Santiago's grid string read whole inches segment by segment while its overall read
 * 90'-11 15/16": grids had been SNAPPED to wall faces / underlay lines whose
 * coordinates carried arbitrary decimals (0.008", 0.028"), and the snap carried the
 * decimal exactly — invisible per segment, visible only in the sum.
 *
 * The model-wide rule (resolution.ts, now 1/64") quantizes FREE input only and keeps a
 * snapped landing bit-exact on what it snapped to — Revit's behaviour, and the
 * Smith-kitchen lesson: rounding a wall end off a diagonal edge it meets draws a jog.
 * DATUMS are the exception (Matt approved, 2026-10-08): grids and level elevations are
 * the references everything else is measured FROM, so they land on the 1/64" lattice
 * EVEN WHEN SNAPPED. A grid is a line, not a joint — no linework meets it, so pulling
 * it ≤ 1/128" onto the lattice can never open a seam.
 *
 * ONE enforcement point, not one per tool: `quantizeTransaction` runs as the LAST
 * post-transact hook of every outermost kernel `transact` (doc.ts) and snaps only the
 * grid/level records that transaction touched — draw, grip drag, typed move, copy,
 * dimension edit and the EQ rule all go through transact, so none can leave an
 * off-lattice datum. `quantizeDatums` is the sweep for models that predate this
 * round (run on open by the Modeler; idempotent — a clean model writes nothing).
 *
 * WHAT is a length — a structural rule, not a field list: any number whose key ends
 * in `Ft` (`elevationFt`, `zExtentFt`), and any 2/3-number array under a point key
 * (`a`, `b`, `pt`, …). Grid head offsets (`headOff`, annotation placement) are skipped.
 */
import * as Y from 'yjs'
import { maps, registerPostTransactHook, type ModelDoc } from './doc'
import { quantFt } from './resolution'

const POINT_KEYS = new Set(['pt', 'pts', 'a', 'b', 'seed', 'poly', 'at', 'worldPt'])
const SKIP_KEYS = new Set(['headOff', 'labelOffset', 'leaders', 'textOffsets', 'bend', 'elbow'])
/** the datum record maps that always sit on the lattice */
const DATUM_MAPS = ['grids', 'levels'] as const
type DatumMap = (typeof DATUM_MAPS)[number]

const isNumArr = (v: unknown): v is number[] =>
  Array.isArray(v) && v.length > 0 && v.every((x) => typeof x === 'number')

/**
 * The value with every length/point inside it on the lattice. Returns the SAME
 * reference when nothing moved, so callers write only real changes (no churn, no
 * spurious remote updates).
 */
export function quantizeValue(v: unknown, key: string | null = null): unknown {
  if (typeof v === 'number') {
    if (key && key.endsWith('Ft')) {
      const q = quantFt(v)
      return q === v ? v : q
    }
    return v
  }
  if (Array.isArray(v)) {
    if (key && POINT_KEYS.has(key) && isNumArr(v) && (v.length === 2 || v.length === 3)) {
      let changed = false
      const out = v.map((x) => { const q = quantFt(x); if (q !== x) changed = true; return q })
      return changed ? out : v
    }
    // a list inherits its key: `pts: [[x,y],…]`, `zExtentFt: [lo, hi]`
    let changed = false
    const out = v.map((x) => { const q = quantizeValue(x, key); if (q !== x) changed = true; return q })
    return changed ? out : v
  }
  if (v && typeof v === 'object' && !(v instanceof Y.AbstractType)) {
    let out: Record<string, unknown> | null = null
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
      if (SKIP_KEYS.has(k)) continue
      const q = quantizeValue(x, k)
      if (q !== x) { out ??= { ...(v as Record<string, unknown>) }; out[k] = q }
    }
    return out ?? v
  }
  return v
}

/** rewrite one datum record's off-lattice fields in place; returns fields changed */
function quantizeRecord(rec: Y.Map<unknown>): number {
  const writes: [string, unknown][] = []
  rec.forEach((v, k) => {
    if (k === 'id' || SKIP_KEYS.has(k) || v instanceof Y.AbstractType) return
    const q = quantizeValue(v, k)
    if (q !== v) writes.push([k, q])
  })
  for (const [k, q] of writes) rec.set(k, q)
  return writes.length
}

/** post-transact: snap every grid/level record this transaction touched (same
 *  transaction, so one undo step with the edit that caused it) */
export function quantizeTransaction(model: ModelDoc, tr?: Y.Transaction): void {
  if (!tr) return
  const m = maps(model)
  for (const name of DATUM_MAPS) {
    const top = m[name] as Y.Map<Y.Map<unknown>>
    const ids = new Set<string>()
    tr.changed.forEach((keys, type) => {
      if (type === (top as unknown)) keys.forEach((k) => { if (k) ids.add(k) })
      else if (type.parent === (top as unknown) && type._item?.parentSub) ids.add(type._item.parentSub)
    })
    for (const id of ids) {
      const rec = top.get(id)
      if (rec instanceof Y.Map) quantizeRecord(rec)
    }
  }
}

/** the sweep: every grid and level onto the lattice. Idempotent; returns fields
 *  changed (0 = already clean, nothing written). Call inside a kernel transact. */
export function quantizeDatums(model: ModelDoc): number {
  const m = maps(model)
  let n = 0
  for (const name of DATUM_MAPS) {
    (m[name] as Y.Map<Y.Map<unknown>>).forEach((rec) => { if (rec instanceof Y.Map) n += quantizeRecord(rec) })
  }
  return n
}

registerPostTransactHook(quantizeTransaction, { last: true })
